import { useEffect, useRef, useState } from 'react'

import { useI18N } from '@susu/i18n/client'
import {
  Button,
  InputField,
  InputPhoneNumber,
  Loader,
} from '@susu/ui/components'

import avo from '../avo/avo'
import { useCustomerData } from '../contexts/CustomerDataContext'
import { useCustomerHub } from '../contexts/CustomerHubContext'
import { useModal } from '../contexts/ModalContext'
import { getResolvedCountryCode } from '../lib/urlCountryCode'
import { debug } from '../utils/debug'

import type { CustomerData } from '../types/customer'

/**
 * Derives the default phone country code.
 * Priority: countryCode query param -> formData.countryCode -> country segment of URL locale -> nl.
 */
function getDefaultPhoneCountryCode(fallback: string): string {
  return getResolvedCountryCode(fallback)
}

interface CustomerInfoModalProps {
  customerData: CustomerData
}

export interface CustomerFormData {
  firstName: string
  lastName: string
  email: string
  phone: string
  countryCode: string
}

export default function CustomerInfoModal({
  customerData,
}: CustomerInfoModalProps) {
  const { t } = useI18N()
  const { updateCustomer, isUpdating } = useCustomerData()
  const { closeModal } = useModal()
  const { role, tracking } = useCustomerHub()
  const [hasSubmitted, setHasSubmitted] = useState(false)
  const [wasUpdating, setWasUpdating] = useState(false)
  const [isPhoneValid, setIsPhoneValid] = useState(true)
  const phoneInitializedRef = useRef(false)
  // Bump this key to force-remount InputPhoneNumber (uncontrolled input) when
  // we need to programmatically clear it after detecting an invalid prefilled value.
  const [phoneInputKey, setPhoneInputKey] = useState(0)
  const [errors, setErrors] = useState<{
    firstName?: string
    lastName?: string
    phone?: string
  }>({})

  const validate = () => {
    const newErrors: { firstName?: string; lastName?: string; phone?: string } =
      {}
    if (!formData.firstName.trim())
      newErrors.firstName = t('customerInfo.validation.firstNameRequired')
    if (!formData.lastName.trim())
      newErrors.lastName = t('customerInfo.validation.lastNameRequired')
    if (!formData.phone.trim()) {
      newErrors.phone = t('customerInfo.validation.phoneRequired')
    } else if (!isPhoneValid) {
      newErrors.phone = t('customerInfo.validation.invalidPhone')
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Only pre-fill phone if it already has a + country code prefix.
  // Local-format numbers (e.g. "0687853883") are cleared immediately so the
  // user always starts from a valid blank state.
  const rawPhone = customerData.phone ?? ''
  const [formData, setFormData] = useState<CustomerFormData>({
    firstName: customerData.firstName ?? '',
    lastName: customerData.lastName ?? '',
    email: customerData.email ?? '',
    phone: rawPhone.startsWith('+') ? rawPhone : '',
    countryCode: customerData.countryCode ?? '',
  })

  const handleInputChange = (field: keyof CustomerFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault()

    if (hasSubmitted || isUpdating) return
    if (!validate()) return

    // Mark as submitted first
    setHasSubmitted(true)

    // Call update customer API
    updateCustomer({
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      phone: formData.phone,
      countryCode: formData.countryCode,
    })
  }

  // Track when mutation starts
  useEffect(() => {
    if (hasSubmitted && isUpdating) {
      debug.log('[CustomerInfoModal] Mutation started, isUpdating:', isUpdating)
      setWasUpdating(true)
    }
  }, [hasSubmitted, isUpdating])

  // Close modal when update completes successfully
  useEffect(() => {
    debug.log('[CustomerInfoModal] State:', {
      hasSubmitted,
      wasUpdating,
      isUpdating,
    })

    // Only close if:
    // 1. We submitted the form
    // 2. The mutation was updating (to ensure it actually ran)
    // 3. The mutation is no longer updating (completed)
    if (hasSubmitted && wasUpdating && !isUpdating) {
      debug.log(
        '[CustomerInfoModal] Customer info updated successfully, closing modal',
      )
      const userType =
        role === 'customer'
          ? [avo.UserType.CUSTOMER]
          : [avo.UserType.STYLE_ADVISOR]
      avo.customerInformationEdited({
        eventLabel: null,
        eventCategory: null,
        eventLocation: 'customer_hub_settings',
        employeeId: tracking?.employeeId ?? '',
        userType,
      })
      closeModal()
    }
  }, [hasSubmitted, wasUpdating, isUpdating, closeModal, role, tracking])

  return (
    <form
      className="flex flex-col pt-20 h-full"
      onSubmit={handleSave}
      data-testid="customer-info-form"
    >
      <div className="flex flex-1 flex-col items-center px-16 lg:px-20 3xl:px-120 overflow-y-auto">
        {/* Form fields */}
        <div className="flex flex-col w-full items-start gap-20 pt-30 pb-60">
          {/* First name field */}
          <InputField
            id="first-name"
            label={t('customerInfo.firstName')}
            type="text"
            name="name"
            value={formData.firstName}
            onChange={(e) => {
              handleInputChange('firstName', e.target.value)
              if (errors.firstName)
                setErrors((prev) => ({ ...prev, firstName: undefined }))
            }}
            onFocus={() => {}}
            onBlur={() => {}}
            error={errors.firstName}
            data-testid="first-name-input"
          />

          {/* Last name field */}
          <InputField
            id="last-name"
            label={t('customerInfo.lastName')}
            type="text"
            name="family-name"
            value={formData.lastName}
            onChange={(e) => {
              handleInputChange('lastName', e.target.value)
              if (errors.lastName)
                setErrors((prev) => ({ ...prev, lastName: undefined }))
            }}
            onFocus={() => {}}
            onBlur={() => {}}
            error={errors.lastName}
            data-testid="last-name-input"
          />

          {/* Email field */}
          <InputField
            id="email"
            label={t('customerInfo.emailAddress')}
            type="email"
            name="email"
            value={formData.email}
            disabled
            onChange={(e) => handleInputChange('email', e.target.value)}
            onFocus={() => {}}
            onBlur={() => {}}
            data-testid="email-input"
          />

          {/* Phone number field with country code */}
          <div className="w-full -mt-10">
            <InputPhoneNumber
              key={phoneInputKey}
              countryCode={
                formData.phone?.startsWith('+')
                  ? undefined
                  : getDefaultPhoneCountryCode(formData.countryCode)
              }
              value={formData.phone}
              onChange={(e) => {
                handleInputChange('phone', e.target.value)
                if (errors.phone)
                  setErrors((prev) => ({ ...prev, phone: undefined }))
              }}
              onFullNumberChange={(fullNumber) =>
                handleInputChange('phone', fullNumber)
              }
              onValidityChange={(isValid) => {
                if (!phoneInitializedRef.current) {
                  phoneInitializedRef.current = true
                  // No-prefix numbers are already cleared at useState init time.
                  // Here we only need to handle + prefixed but invalid numbers.
                  if (!isValid && formData.phone.trim()) {
                    handleInputChange('phone', '')
                    setIsPhoneValid(true)
                    // InputPhoneNumber is uncontrolled (defaultValue), so bump
                    // the key to remount it — the only reliable way to reset
                    // the DOM input to empty.
                    setPhoneInputKey((k) => k + 1)
                  }
                  return
                }
                setIsPhoneValid(isValid)
                if (!isValid && formData.phone.trim()) {
                  setErrors((prev) => ({
                    ...prev,
                    phone: t('customerInfo.validation.invalidPhone'),
                  }))
                } else if (isValid) {
                  setErrors((prev) => ({ ...prev, phone: undefined }))
                }
              }}
              error={errors.phone}
              data-testid="phone-number"
            />
          </div>
        </div>
      </div>

      <button type="submit" hidden aria-hidden="true" tabIndex={-1} />

      <div className="border-t border-border p-16 lg:p-20">
        <Button
          size="l"
          variant="filled"
          onClick={isUpdating || hasSubmitted ? undefined : handleSave}
          aria-disabled={isUpdating || hasSubmitted}
          className={`w-full ${isUpdating || hasSubmitted ? 'pointer-events-none opacity-50' : ''}`}
          data-testid="customer-info-form-submit"
        >
          {isUpdating || hasSubmitted ? (
            <Loader
              ariaLabel={t('customerInfo.savingInfo')}
              className="h-auto"
            />
          ) : (
            t('customerInfo.saveInfo')
          )}
        </Button>
      </div>
    </form>
  )
}
